import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ChartPieSlice, Users, CalendarCheck, Buildings, MapPinLine, ChartBar,
  Sparkle, SignOut, List, CurrencyInr, UserGear
} from "@phosphor-icons/react";
import { useState } from "react";

const nav = [
  { to: "/", label: "Dashboard", icon: ChartPieSlice, testid: "sidebar-dashboard" },
  { to: "/leads", label: "Leads", icon: Users, testid: "sidebar-leads" },
  { to: "/followups", label: "Follow-ups", icon: CalendarCheck, testid: "sidebar-followups" },
  { to: "/properties", label: "Inventory", icon: Buildings, testid: "sidebar-properties" },
  { to: "/site-visits", label: "Site Visits", icon: MapPinLine, testid: "sidebar-sitevisits" },
  { to: "/sales", label: "Sales", icon: CurrencyInr, testid: "sidebar-sales" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "sidebar-reports" },
  { to: "/users", label: "Users", icon: UserGear, testid: "sidebar-users", adminOnly: true },
  { to: "/ai-assistant", label: "AI Assistant", icon: Sparkle, testid: "sidebar-ai" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav_go = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); nav_go("/login"); };

  return (
    <div className="min-h-screen flex noise-overlay" data-testid="app-shell">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0A1120] border-r border-[#1E293B] flex flex-col transform transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-20 flex items-center gap-3 px-6 border-b border-[#1E293B]">
          <img src="/logo.png" alt="Nawab Properties" data-testid="sidebar-logo" className="w-10 h-10 rounded-lg bg-white object-contain gold-glow" />
          <div className="leading-tight" data-testid="sidebar-brand">
            <div className="text-[13px] font-bold text-white tracking-wide">NAWAB PROPERTIES</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]">In Partnership With —</div>
            <div className="text-[13px] font-bold text-white tracking-wide">RISING SUN BUILDCON</div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]">Properties CRM</div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
          {nav.filter(item => !item.adminOnly || user?.role === "admin").map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
                    isActive
                      ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20"
                      : "text-slate-300 hover:bg-white/5 border border-transparent"
                  }`
                }
              >
                <Icon weight="duotone" size={20} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#1E293B]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#1E293B] border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] font-semibold text-sm">
              {user?.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#D4AF37] py-2 border border-[#1E293B] rounded-md transition-colors"
          >
            <SignOut size={14} weight="duotone" /> Sign out
          </button>
        </div>
      </aside>

      {/* overlay for mobile */}
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 lg:px-8 bg-[#060B14]/80 backdrop-blur-xl border-b border-[#1E293B]">
          <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <List size={24} />
          </button>
          <div className="hidden lg:block">
            <div className="text-sm text-white font-serif">Luxury CRM • Mohali</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline-flex text-xs uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/30 px-3 py-1 rounded-full">
              {user?.role === "admin" ? "Admin" : "Executive"}
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8 relative z-10" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
